// THE CONVERSATION HELD ABOUT ONE OPEN FILE (Figma "Task creation design" frames 70–73 / m70–m73,
// Pouria 2026-10-15; td-audit/S4-S5-CONTRACT.md).
//
// WHICH file is open in the box is store/modules/fileBriefing.js's (the "Briefing <file>" card,
// stream S4): its record, its Ongoing, its possible follow-ups. This module is the conversation's
// half, read OVER that one — exactly as store/modules/emailConversation.js reads over the open
// email — and it keeps only what the chat does not know:
//
//   • WHICH file the turns in the middle are about (`documentId`), so a question asked under a
//     different file clears them: a conversation belongs to the file it was held about;
//   • the derived facts the middle, the strip and the answer read — the header's
//     "<Ongoing> . conversation", the scope the question goes out in, and the file the answer's own
//     "N ATTACHMENT" chips are filtered against.
//
// The turns themselves are store/modules/timelineSearch.js's, drawn by components/Home/AiAnswer.vue,
// asked with the scope lib/fileConversation.js writes ("object:document:<id>", busy-main-bff#20 —
// and, since this batch, the BFF reads that document's extracted text for the question).

import { fileConversationAnswered, fileConversationScope, fileConversationTitle } from '@/lib/fileConversation'

// A FACTORY, not a shared object (as emailConversation.js): two stores in one spec must not share.
export const state = () => ({
  /** the document id the turns in the middle belong to, or '' when nothing has been asked */
  documentId: '',
})

const clean = (v) => String(v ?? '').replace(/\s+/g, ' ').trim()

/** The open file's record, as the briefing module holds it (null when no file is open). */
const openDoc = (rootGetters) => (rootGetters?.['fileBriefing/open'] ? rootGetters['fileBriefing/doc'] || null : null)

export const getters = {
  /** A file Briefing is open in the box: the middle is its conversation. */
  active: (s, g, rootState, rootGetters) => !!rootGetters?.['fileBriefing/open'],
  documentId: (s, g, rootState, rootGetters) => clean(rootGetters?.['fileBriefing/documentId']),
  fileName: (s, g, rootState, rootGetters) => clean(openDoc(rootGetters)?.filename),
  contentType: (s, g, rootState, rootGetters) => clean(openDoc(rootGetters)?.contentType),
  /** busy-document-srv's class word for it ("receipt", "pdf", …) — the running line's noun. */
  kind: (s, g, rootState, rootGetters) => clean(openDoc(rootGetters)?.class).toLowerCase(),
  ongoingTitle: (s, g, rootState, rootGetters) => clean(rootGetters?.['fileBriefing/ongoingTitle']),
  scope: (s, g) => fileConversationScope(g.documentId),
  title: (s, g) => fileConversationTitle(g.ongoingTitle),
  mobileTitle: (s, g) => fileConversationTitle(g.ongoingTitle, { mobile: true }),
  /** The file the answer's own sources are filtered against (lib/fileConversation `withoutOwnFile`). */
  file: (s, g) => (g.documentId ? { documentId: g.documentId, fileName: g.fileName } : null),
  /** At least one answered turn about THIS file (frame 72: the card drops its follow-ups; m72: it folds). */
  answered: (s, g, rootState, rootGetters) =>
    !!g.documentId && s.documentId === g.documentId
    && fileConversationAnswered(rootGetters?.['timelineSearch/conversation'] || []),
}

export const mutations = {
  SET_DOCUMENT(s, id) { s.documentId = clean(id) },
}

export const actions = {
  /**
   * A question was asked under the file box — typed, sent, or a follow-up clicked. It runs in the
   * file's scope and lands in the middle. A different file than the one the turns belong to clears
   * them first.
   */
  async ask({ state: s, getters: g, commit, dispatch }, { question } = {}) {
    const id = g.documentId
    const text = clean(question)
    if (!id || !text) return false
    if (s.documentId && s.documentId !== id) await dispatch('timelineSearch/clearConversation', null, { root: true })
    commit('SET_DOCUMENT', id)
    await dispatch('timelineSearch/ask', { question: text, scope: fileConversationScope(id) }, { root: true })
    return true
  },

  /** The file left the box (the gutter's ✕ Close, the Home launcher): its conversation goes with it. */
  close({ commit, dispatch, state: s }) {
    if (s.documentId) dispatch('timelineSearch/clearConversation', null, { root: true })
    commit('SET_DOCUMENT', '')
  },
}

export default { namespaced: true, state, getters, actions, mutations }
